/**
 * MONSTER TOWN — VENUE #2, THE RESTAURANT. PURE DATA DROP.
 *
 * THIS FILE IS THE PROOF OF THE CLAIM. The venue system was written so that a
 * second venue is content, not code: nothing in `src/venue/service.ts`,
 * `orders.ts` or `src/economy/**` knows a restaurant exists. Everything below is
 * a menu, a set of placeables, and one `defineVenue`.
 *
 * WHAT CHANGED AFTER ROUND 2, AND WHY. The first version of this file satisfied
 * "data drop" mechanically and failed it visually: it reused the ice cream
 * shop's fixture art unchanged, so the restaurant shipped as the same round
 * bistro tables, the same mushroom stools, the same counter, the same teal bin
 * and the same checkerboard floor in tan instead of mint. A kid would not have
 * believed they had walked in somewhere new — and "it must always look good" is
 * a law, not a polish pass.
 *
 * The fix was NOT engine code. It is more data: this venue now declares its own
 * kitchen and dining-room art (`src/venue/kitchenArt.ts`), its own floor
 * pattern, and its own fixtures tagged with the ROLES the service loop reads
 * (`venue:bell`, `venue:clear`, `venue:hint`). The shared pieces in
 * `venueFloor.ts` are still there for any venue that wants them.
 */

import { FOOT_1x1, FOOT_2x1, type Cell } from '../core/grid'
import { definePlaceable } from '../core/placeable'
import type { PaletteName } from '../core/palette'
import type { PlaceableId, SceneId, VenueId } from '../core/types'
import { toppingJarArt } from '../venue/fixtures'
import {
  diningChairArt,
  diningTableArt,
  passCounterArt,
  plateRackArt,
  scrapPailArt,
  specialsBoardArt,
  stoveArt,
} from '../venue/kitchenArt'
import { defineVenue, type VenueDef, type VenueItem } from '../venue/types'
import { RESTAURANT_SCENE, RESTAURANT_VENUE, venueExit } from './venue'

const id = (s: string): PlaceableId => s as PlaceableId
const cell = (gx: number, gz: number): Cell => ({ gx, gy: 0, gz })

// ---------------------------------------------------------------------------
// the menu — same three steps, different pictures
// ---------------------------------------------------------------------------

/**
 * `base_plate` is a `plate`, not a `cup`.
 *
 * It was declared as a cup, which is why dinner used to be served in an ice
 * cream tub with the food stacked into a vertical tower, and why the order cards
 * showed a plate next to a pink ice-cream cone. Shape is what decides both the
 * 3D serving and the picture on the card, and `plate` also switches the serving
 * builder from STACKING to laying the portions out side by side.
 */
export const RESTAURANT_ITEMS: readonly VenueItem[] = [
  { id: 'base_plate', kind: 'base', color: 'plate', shape: 'plate', stationDefId: id('rest_plates') },
  { id: 'food_pasta', kind: 'scoop', color: 'roofMustard', shape: 'ball', stationDefId: id('rest_pot_pasta') },
  { id: 'food_greens', kind: 'scoop', color: 'leafLight', shape: 'ball', stationDefId: id('rest_pot_greens') },
  { id: 'food_soup', kind: 'scoop', color: 'roofRed', shape: 'ball', stationDefId: id('rest_pot_soup') },
  { id: 'food_fish', kind: 'scoop', color: 'monsterSky', shape: 'ball', stationDefId: id('rest_pot_fish') },
  { id: 'rest_top_sauce', kind: 'topping', color: 'flowerRed', shape: 'sauce', stationDefId: id('rest_sauce') },
  { id: 'rest_top_cheese', kind: 'topping', color: 'flowerYellow', shape: 'sprinkles', stationDefId: id('rest_cheese') },
]

// ---------------------------------------------------------------------------
// the kitchen
// ---------------------------------------------------------------------------

/** A cooking range. Was an ice cream freezer cabinet with a coloured blob. */
function pot(defId: string, itemId: string, color: PaletteName, cost: number, tier?: string) {
  return definePlaceable<{ scoops: number }>({
    id: id(defId),
    devName: `Cooking range (${itemId})`,
    category: 'venueFixture',
    scale: 'interior',
    footprint: FOOT_1x1,
    cost,
    ...(tier === undefined ? {} : { tier }),
    stackable: false,
    walkable: false,
    tints: [color, 'metalDark'],
    tags: ['venue', 'station', 'scoop', 'restaurant'],
    initialState: () => ({ scoops: 0 }),
    buildArt: (ctx) => stoveArt(ctx, color),
    interact: (ctx) => ({
      feedback: [
        { kind: 'anim', clip: 'stir', seconds: 0.45, target: 'lever' },
        { kind: 'anim', clip: 'bubble', seconds: 0.4, target: 'dome' },
        { kind: 'particles', effect: 'steam', count: 5 },
        { kind: 'spawn', what: `venue:add:${itemId}` },
        { kind: 'sound', cue: 'ladle' },
        { kind: 'state', patch: { scoops: ctx.state.scoops + 1 } },
      ],
      cooldownMs: 160,
    }),
  })
}

function jar(defId: string, itemId: string, cost: number, tier?: string) {
  const item = RESTAURANT_ITEMS.find((i) => i.id === itemId) as VenueItem
  return definePlaceable<{ used: number }>({
    id: id(defId),
    devName: `Restaurant topping (${itemId})`,
    category: 'venueFixture',
    scale: 'interior',
    footprint: FOOT_1x1,
    cost,
    ...(tier === undefined ? {} : { tier }),
    stackable: false,
    walkable: false,
    tints: [item.color, 'plate'],
    tags: ['venue', 'station', 'topping', 'restaurant'],
    initialState: () => ({ used: 0 }),
    buildArt: (ctx) => toppingJarArt(ctx, item),
    interact: (ctx) => ({
      feedback: [
        { kind: 'anim', clip: 'pop', seconds: 0.35, target: 'lid' },
        { kind: 'particles', effect: 'sparkle', count: 6 },
        { kind: 'spawn', what: `venue:add:${itemId}` },
        { kind: 'sound', cue: 'topping_shake' },
        { kind: 'state', patch: { used: ctx.state.used + 1 } },
      ],
      cooldownMs: 160,
    }),
  })
}

export const restPlates = definePlaceable<{ taken: number }>({
  id: id('rest_plates'),
  devName: 'Plate dresser',
  category: 'venueFixture',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['plate', 'glassDark'],
  tags: ['venue', 'station', 'base', 'restaurant'],
  initialState: () => ({ taken: 0 }),
  buildArt: (ctx) => plateRackArt(ctx),
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'lift', seconds: 0.4, target: 'holder' },
      { kind: 'particles', effect: 'sparkle', count: 4 },
      { kind: 'spawn', what: 'venue:add:base_plate' },
      { kind: 'sound', cue: 'plate_clink' },
      { kind: 'state', patch: { taken: ctx.state.taken + 1 } },
    ],
    cooldownMs: 160,
  }),
})

export const potPasta = pot('rest_pot_pasta', 'food_pasta', 'roofMustard', 0)
export const potGreens = pot('rest_pot_greens', 'food_greens', 'leafLight', 0)
export const potSoup = pot('rest_pot_soup', 'food_soup', 'roofRed', 60)
export const potFish = pot('rest_pot_fish', 'food_fish', 'monsterSky', 90, 'tier1')
export const restSauce = jar('rest_sauce', 'rest_top_sauce', 70)
export const restCheese = jar('rest_cheese', 'rest_top_cheese', 85, 'tier2')

// ---------------------------------------------------------------------------
// the dining room — its own furniture, tagged with the roles the loop reads
// ---------------------------------------------------------------------------

export const restPass = definePlaceable<{ rings: number }>({
  id: id('rest_pass'),
  devName: 'Kitchen pass',
  category: 'venueFixture',
  scale: 'interior',
  footprint: FOOT_2x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['wallCream', 'wallBlue', 'metal'],
  tags: ['venue', 'starter', 'restaurant', 'venue:bell'],
  initialState: () => ({ rings: 0 }),
  buildArt: (ctx) => passCounterArt(ctx),
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'ring', seconds: 0.5, target: 'bell' },
      { kind: 'particles', effect: 'note', count: 5 },
      { kind: 'spawn', what: 'venue:bell' },
      { kind: 'sound', cue: 'bell_ding' },
      { kind: 'state', patch: { rings: ctx.state.rings + 1 } },
    ],
    cooldownMs: 400,
  }),
})

export const restTable = definePlaceable<{ served: number }>({
  id: id('rest_table'),
  devName: 'Laid table for two',
  category: 'furniture',
  scale: 'interior',
  footprint: FOOT_2x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['wallCream', 'wallPink', 'wallMint', 'wallLilac'],
  tags: ['venue', 'seat', 'starter', 'restaurant'],
  initialState: () => ({ served: 0 }),
  buildArt: (ctx) => diningTableArt(ctx),
  // LAW #1: tapping a table always rattles the cutlery; if someone is sitting
  // there hoping for dinner, it also hands them what you are carrying.
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'wobble', seconds: 0.4, target: 'top' },
      { kind: 'anim', clip: 'flicker', seconds: 0.5, target: 'flame' },
      { kind: 'particles', effect: 'sparkle', count: 4 },
      { kind: 'spawn', what: 'venue:serve' },
      { kind: 'sound', cue: 'table_tap' },
      { kind: 'state', patch: { served: ctx.state.served + 1 } },
    ],
    cooldownMs: 300,
  }),
})

export const restChair = definePlaceable<{ served: number }>({
  id: id('rest_chair'),
  devName: 'Dining chair',
  category: 'furniture',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['fabricAlt', 'fabric', 'cushion', 'wallMint'],
  tags: ['venue', 'seat', 'starter', 'restaurant'],
  initialState: () => ({ served: 0 }),
  buildArt: (ctx) => diningChairArt(ctx),
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'wobble', seconds: 0.4, target: 'seat' },
      { kind: 'particles', effect: 'sparkle', count: 4 },
      { kind: 'spawn', what: 'venue:serve' },
      { kind: 'sound', cue: 'table_tap' },
      { kind: 'state', patch: { served: ctx.state.served + 1 } },
    ],
    cooldownMs: 300,
  }),
})

export const restPail = definePlaceable<{ dumped: number }>({
  id: id('rest_pail'),
  devName: 'Scraps pail',
  category: 'venueFixture',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['wood', 'woodDark', 'metalDark'],
  tags: ['venue', 'starter', 'restaurant', 'venue:clear'],
  initialState: () => ({ dumped: 0 }),
  buildArt: (ctx) => scrapPailArt(ctx),
  // A mis-tap is never punished: this is the friendly undo for a wonky dinner.
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'flip', seconds: 0.4, target: 'lid' },
      { kind: 'particles', effect: 'poof', count: 7 },
      { kind: 'spawn', what: 'venue:clear' },
      { kind: 'sound', cue: 'poof' },
      { kind: 'state', patch: { dumped: ctx.state.dumped + 1 } },
    ],
    cooldownMs: 250,
  }),
})

export const restBoard = definePlaceable<{ hints: number }>({
  id: id('rest_board'),
  devName: 'Specials board',
  category: 'venueFixture',
  scale: 'interior',
  footprint: FOOT_1x1,
  cost: 60,
  stackable: false,
  walkable: false,
  tints: ['woodLight', 'wood', 'inkSoft'],
  tags: ['venue', 'decor', 'restaurant', 'venue:hint'],
  initialState: () => ({ hints: 0 }),
  buildArt: (ctx) => specialsBoardArt(ctx, RESTAURANT_ITEMS),
  // Tap it and every waiting order bounces — the "what did they want again?"
  // button. Memory help, never a penalty for forgetting.
  interact: (ctx) => ({
    feedback: [
      { kind: 'anim', clip: 'shine', seconds: 0.6, target: 'board' },
      { kind: 'particles', effect: 'sparkle', count: 6 },
      { kind: 'spawn', what: 'venue:hint' },
      { kind: 'sound', cue: 'sparkle_soft' },
      { kind: 'state', patch: { hints: ctx.state.hints + 1 } },
    ],
    cooldownMs: 300,
  }),
})

// ---------------------------------------------------------------------------
// the venue
// ---------------------------------------------------------------------------

/**
 * Origin lines cell (8,11) up with the world crew's restaurant front door at
 * world (13, 32), exactly like the ice cream shop.
 */
export const restaurant: VenueDef = defineVenue({
  id: RESTAURANT_VENUE,
  kind: 'restaurant',
  sceneId: RESTAURANT_SCENE,
  devName: 'Restaurant',
  grid: { id: 'venue_restaurant', scale: 'interior', width: 12, depth: 9, levels: 4, originX: 6.5, originZ: 23.5 },
  shell: {
    wall: 'wallPink',
    wallTrim: 'roofRed',
    floor: 'floorWood',
    floorAlt: 'wood',
    roofBeam: 'woodDark',
    // PLANKS, NOT A CHECKERBOARD. The single biggest reason the two venues read
    // as the same room was that both floors were the same diagonal chequer.
    floorStyle: 'planks',
    rug: 'roofRed',
  },
  doorCell: cell(6, 8),
  items: RESTAURANT_ITEMS,
  seatDefIds: [restTable.id, restChair.id],
  maxScoops: 3,
  starter: [
    { defId: venueExit.id, anchor: cell(6, 8) },
    { defId: restPass.id, anchor: cell(5, 3) },
    { defId: restPlates.id, anchor: cell(3, 1) },
    { defId: potPasta.id, anchor: cell(4, 1) },
    { defId: potGreens.id, anchor: cell(7, 1) },
    { defId: restPail.id, anchor: cell(9, 1) },
    { defId: restTable.id, anchor: cell(1, 5) },
    { defId: restTable.id, anchor: cell(8, 5) },
    { defId: restChair.id, anchor: cell(5, 6) },
  ],
})

export const RESTAURANT_CONTENT = [
  restPlates,
  potPasta,
  potGreens,
  potSoup,
  potFish,
  restSauce,
  restCheese,
  restPass,
  restTable,
  restChair,
  restPail,
  restBoard,
]

/** A kid can always lay out and run the restaurant with no coins at all. */
export const RESTAURANT_FREE_IDS: PlaceableId[] = [
  restPlates.id,
  potPasta.id,
  potGreens.id,
  restPass.id,
  restTable.id,
  restChair.id,
  restPail.id,
]

export const RESTAURANT_SCENE_ID: SceneId = RESTAURANT_SCENE
export const RESTAURANT_VENUE_ID: VenueId = RESTAURANT_VENUE
